import React from 'react';
import CatalogItemForm from './catalog/components/CatalogItemForm';
import CreatorAuthorityForm from './catalog/components/CreatorAuthorityForm';
import CollectionsCatalog from './catalog/components/CollectionsCatalog';
import CreatorsListViewer from './catalog/components/creators_list_viewer';
import AdminAnalytics from './catalog/components/admin_analytics';
import VisitorAnalytics from './catalog/components/VisitorAnalytics';
import { recordAdminAnalyticsEvent } from './services/supabaseAnalytics';
import { parseBrowser, parseDevice, fetchClientGeo, sessionId } from './catalog/components/services/analyticsUtils';
import { AnalyticsProvider } from './AnalyticsContext';

type ViewKey = 'collections' | 'catalog' | 'list_creators' | 'creators' | 'admin' | 'visitors';
const ADMIN_HASH = '#admin';
const ADMIN_ACCESS_KEY = process.env.REACT_APP_ADMIN_ACCESS_KEY;

function AppInner() {
  const getInitialView = React.useCallback((): ViewKey => {
    return window.location.hash === ADMIN_HASH ? 'admin' : 'collections';
  }, []);
  const [currentView, setCurrentView] = React.useState<ViewKey>(getInitialView);
  const [adminUnlocked, setAdminUnlocked] = React.useState<boolean>(false);

  const navItems: Array<{ key: ViewKey; label: string }> = [
    { key: 'collections', label: 'Collections' },
    { key: 'catalog', label: 'Catalog Items' },
    { key: 'list_creators', label: 'Creators List' },
    { key: 'creators', label: 'Creators' }
  ];

  React.useEffect(() => {
    const syncFromHash = () => {
      if (window.location.hash === ADMIN_HASH) {
        setCurrentView('admin');
      } else {
        setCurrentView((prev) => (prev === 'admin' ? 'collections' : prev));
      }
    };
    window.addEventListener('hashchange', syncFromHash);
    syncFromHash();
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  const adminPromptShown = React.useRef(false);

  React.useEffect(() => {
    if (currentView !== 'admin') {
      setAdminUnlocked(false);
      adminPromptShown.current = false;
      return;
    }
    if (adminPromptShown.current) return;
    adminPromptShown.current = true;
    const entered = window.prompt('Admin access key');
    const expected = ADMIN_ACCESS_KEY || 'rarebooks-admin';
    if (entered && entered === expected) {
      setAdminUnlocked(true);
      return;
    }
    setAdminUnlocked(false);
    window.location.hash = '';
    setCurrentView('collections');
  }, [currentView]);

  // Prefetch geo on mount so it's ready before any event fires
  React.useEffect(() => { fetchClientGeo(); }, []);

  // Track page views on every tab change
  const trackedView = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (currentView === 'admin') return;
    if (trackedView.current === currentView) return;
    trackedView.current = currentView;
    fetchClientGeo().then((geo) => {
      recordAdminAnalyticsEvent({
        eventType: 'page_view',
        page: currentView,
        label: currentView,
        // Top-level geo columns — written directly to dedicated DB columns
        city:         geo.city,
        region:       geo.region,
        country:      geo.country,
        country_code: geo.country_code,
        lat:          geo.lat,
        lng:          geo.lng,
        // metadata keeps browser/device/session info (and geo as backup for old rows)
        metadata: {
          page:     currentView,
          browser:  parseBrowser(navigator.userAgent),
          device:   parseDevice(navigator.userAgent),
          referrer: document.referrer ? new URL(document.referrer).hostname : 'Direct',
          session_id: sessionId,
          location: geo.location,
          city:     geo.city,
          region:   geo.region,
          country:  geo.country,
          country_code: geo.country_code,
          lat:      geo.lat,
          lng:      geo.lng,
        }
      });
    });
  }, [currentView]);

  // Track clicks via event delegation
  const handleContentClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (currentView === 'admin' || currentView === 'visitors') return;
    const target = e.target as HTMLElement;
    const clickable = target.closest('button, a, [role="button"], [data-track]') as HTMLElement | null;
    if (!clickable) return;

    const label =
      clickable.getAttribute('data-track') ||
      clickable.getAttribute('aria-label') ||
      clickable.textContent?.trim().slice(0, 60) ||
      'click';

    // For clicks we record geo too so the visitor map works for click events
    fetchClientGeo().then((geo) => {
      recordAdminAnalyticsEvent({
        eventType: 'click',
        page: currentView,
        label,
        city:         geo.city,
        region:       geo.region,
        country:      geo.country,
        country_code: geo.country_code,
        lat:          geo.lat,
        lng:          geo.lng,
        metadata: {
          page:     currentView,
          label,
          tag:      clickable.tagName.toLowerCase(),
          href:     clickable.getAttribute('href') || '',
          browser:  parseBrowser(navigator.userAgent),
          device:   parseDevice(navigator.userAgent),
          session_id: sessionId,
          location: geo.location,
          city:     geo.city,
          region:   geo.region,
          country:  geo.country,
          country_code: geo.country_code,
          lat:      geo.lat,
          lng:      geo.lng,
        }
      });
    });
  }, [currentView]);

  const closeAdmin = () => {
    setAdminUnlocked(false);
    window.location.hash = '';
    setCurrentView('collections');
  };

  return (
    <div className="App min-h-screen bg-stone-100">
      <nav className="sticky top-0 z-20 border-b border-amber-950/20 bg-amber-900 text-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => setCurrentView(item.key)}
                className={[
                  'rounded-full px-4 py-2 text-sm font-medium transition',
                  currentView === item.key
                    ? 'bg-white text-amber-900'
                    : 'bg-transparent text-amber-50 hover:bg-white/10'
                ].join(' ')}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {(currentView === 'admin' || currentView === 'visitors') && adminUnlocked && (
              <button
                onClick={closeAdmin}
                className="rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-amber-50 transition hover:bg-white/10"
              >
                Exit Admin
              </button>
            )}
          </div>
        </div>
      </nav>

      <div onClick={handleContentClick}>
        {currentView === 'collections'  && <CollectionsCatalog />}
        {currentView === 'catalog'      && <CatalogItemForm />}
        {currentView === 'list_creators' && <CreatorsListViewer />}
        {currentView === 'creators'     && <CreatorAuthorityForm />}
        {currentView === 'admin'    && adminUnlocked && <AdminAnalytics onShowVisitors={() => setCurrentView('visitors')} />}
        {currentView === 'visitors' && adminUnlocked && <VisitorAnalytics onBack={() => setCurrentView('admin')} />}
      </div>
    </div>
  );
}

function App() {
  return (
    <AnalyticsProvider>
      <AppInner />
    </AnalyticsProvider>
  );
}

export default App;
